import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { ORIGIN_LABELS, type TicketListItem } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { formatDateTime } from '@/lib/utils';
import { deskDetailQuery, ticketsListQuery } from './tickets-queries';
import { flatDeskListParams, openDeskListParams, type DeskListFilter } from './ticket-list-params';
import { initials } from './ticket-format';
import { EmptyData, SlaChip, StatusChip } from './ticket-chips';
import { listSlaState } from './ticket-sla';
import { capitalize, useTerms } from '@/shared/tenant-profile';

const FLAT_LIMIT = 50;
const GROUPED_LIMIT = 200;

export function TicketList({
  desk,
  status,
  q,
  group,
  responsibleId,
}: {
  desk: number | undefined;
  status: 'open' | 'closed' | 'canceled' | 'all';
  q: string;
  /** v11: filtro por grupo de tickets (undefined = todos). */
  group?: number;
  /** spec 066: "Meus tickets" ligado = o id de quem está logado */
  responsibleId?: number;
}) {
  const filter: DeskListFilter = { desk, q, group, responsibleId };
  if (status === 'open') return <GroupedByStageList filter={filter} />;
  return <FlatList filter={filter} status={status} />;
}

// ── Lista agrupada por estágio (tickets abertos da mesa) ────────────────────

interface StageGroup {
  key: string;
  name: string;
  tickets: TicketListItem[];
}

function GroupedByStageList({ filter }: { filter: DeskListFilter }) {
  const { desk } = filter;
  const deskQ = useQuery({ ...deskDetailQuery(desk ?? 0), enabled: desk !== undefined });
  const listQ = useQuery({
    ...ticketsListQuery(openDeskListParams(filter, GROUPED_LIMIT)),
    enabled: desk !== undefined,
  });

  if (desk === undefined || deskQ.isPending || listQ.isPending) return <ListSkeleton />;
  if (deskQ.isError || listQ.isError) return <EmptyData message="Erro ao carregar tickets" />;

  const { items, total } = listQ.data;
  if (items.length === 0) {
    const mine = filter.responsibleId !== undefined;
    return (
      <EmptyData
        message={mine ? 'Nenhum ticket aberto seu nesta mesa' : 'Nenhum ticket aberto nesta mesa'}
      />
    );
  }

  const byStage = new Map<number, TicketListItem[]>();
  for (const t of items) {
    const arr = byStage.get(t.stage.id);
    if (arr) arr.push(t);
    else byStage.set(t.stage.id, [t]);
  }

  const orderedStages = [...deskQ.data.stages].sort((a, b) => a.position - b.position);
  const groups: StageGroup[] = [];
  for (const stage of orderedStages) {
    const tickets = byStage.get(stage.id);
    if (tickets && tickets.length > 0) {
      groups.push({ key: `s-${stage.id}`, name: stage.name, tickets });
      byStage.delete(stage.id);
    }
  }
  // Estágios fora da mesa (ex.: mudou de mesa durante a busca) vão para o fim.
  for (const [stageId, tickets] of byStage) {
    const first = tickets[0];
    if (first) groups.push({ key: `x-${stageId}`, name: first.stage.name, tickets });
  }

  return (
    <div className="flex flex-col">
      {groups.map((g) => (
        <StageSection key={g.key} name={g.name} tickets={g.tickets} />
      ))}
      {total > items.length ? (
        <p className="px-2 py-2 text-xs text-muted-foreground">
          Exibindo {items.length} de {total} tickets abertos — refine com a busca.
        </p>
      ) : null}
    </div>
  );
}

function StageSection({ name, tickets }: { name: string; tickets: TicketListItem[] }) {
  const [open, setOpen] = useState(true);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-left text-[13px] font-semibold text-foreground transition-colors hover:bg-muted/50">
        {open ? (
          <MinusIcon className="size-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <PlusIcon className="size-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="truncate" title={name}>
          {name}
        </span>
        <span className="font-normal text-muted-foreground">- ({tickets.length})</span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <TicketsTable tickets={tickets} showClosedAt={false} />
      </CollapsibleContent>
    </Collapsible>
  );
}

// ── Lista plana (fechados / cancelados / todos) com paginação ───────────────

function FlatList({
  filter,
  status,
}: {
  filter: DeskListFilter;
  status: 'closed' | 'canceled' | 'all';
}) {
  const [offset, setOffset] = useState(0);

  const listQ = useQuery({
    ...ticketsListQuery(flatDeskListParams(filter, status, FLAT_LIMIT, offset)),
    placeholderData: keepPreviousData,
  });

  if (listQ.isPending) return <ListSkeleton />;
  if (listQ.isError) return <EmptyData message="Erro ao carregar tickets" />;

  const { items, total } = listQ.data;
  if (items.length === 0) return <EmptyData message="Nenhum ticket encontrado" />;

  return (
    <div className="flex flex-col gap-2">
      <TicketsTable tickets={items} showClosedAt />
      <div className="flex items-center justify-between text-[13px] text-muted-foreground">
        <span className="tabular-nums">
          {`${offset + 1}–${Math.min(offset + FLAT_LIMIT, total)}`} de {total}
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0 || listQ.isFetching}
            onClick={() => setOffset(Math.max(0, offset - FLAT_LIMIT))}
          >
            Anterior
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={offset + FLAT_LIMIT >= total || listQ.isFetching}
            onClick={() => setOffset(offset + FLAT_LIMIT)}
          >
            Próxima
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Mini-tabela compartilhada (grupo, lista plana e busca por mesa) ─────────

export function TicketsTable({
  tickets,
  showClosedAt,
  showPlate = false,
}: {
  tickets: Array<TicketListItem & { searchPlate?: string | null }>;
  showClosedAt: boolean;
  /** busca agrupada (spec 012): coluna com o identificador extraído do ticket */
  showPlate?: boolean;
}) {
  const navigate = useNavigate();
  const now = new Date();
  // spec 017 (US2 AS2): a coluna diz o recurso do perfil ("Veículo") e mostra o
  // identificador extraído do atendimento (a placa)
  const resourceLabel = capitalize(useTerms().resource);
  const th = 'h-9 px-3 text-left text-xs font-semibold text-muted-foreground whitespace-nowrap';
  return (
    <div className="mb-2 overflow-x-auto rounded-md border border-border/70">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className={th}>Número</th>
            <th className={th}>Título</th>
            {showPlate ? <th className={th}>{resourceLabel}</th> : null}
            <th className={th}>Cliente</th>
            <th className={th}>Origem</th>
            <th className={th}>Prioridade</th>
            <th className={th}>Status</th>
            <th className={th}>SLA</th>
            <th className={th}>Responsável</th>
            {showClosedAt ? <th className={th}>Fechado em</th> : null}
          </tr>
        </thead>
        <tbody>
          {tickets.map((t) => (
            <tr
              key={t.ticketNumber}
              className="h-10 cursor-pointer border-b transition-colors last:border-0 hover:bg-muted/40"
              onClick={() =>
                void navigate({
                  to: '/tickets/$ticketNumber',
                  params: { ticketNumber: String(t.ticketNumber) },
                })
              }
            >
              <td className="w-24 px-3">
                <Link
                  to="/tickets/$ticketNumber"
                  params={{ ticketNumber: String(t.ticketNumber) }}
                  onClick={(e) => e.stopPropagation()}
                  className="font-medium text-primary hover:underline"
                >
                  {t.ticketNumber}
                </Link>
              </td>
              <td className="max-w-80 truncate px-3 font-medium text-foreground" title={t.title}>
                {t.title}
              </td>
              {showPlate ? (
                <td className="whitespace-nowrap px-3 font-mono text-xs text-foreground">
                  {t.searchPlate ?? <span className="text-muted-foreground">—</span>}
                </td>
              ) : null}
              <td className="max-w-48 truncate px-3 text-muted-foreground" title={t.client.name}>
                {t.client.name}
              </td>
              <td className="whitespace-nowrap px-3 text-muted-foreground">
                {ORIGIN_LABELS[t.createdVia]}
              </td>
              <td className="whitespace-nowrap px-3">{t.priority?.name ?? '—'}</td>
              <td className="px-3">
                <StatusChip kind={t.status.kind} name={t.status.name} />
              </td>
              <td className="px-3">
                {(() => {
                  // Chip só quando relevante: Vencido (vermelho) / A vencer (amarelo).
                  const sla = listSlaState(t, now);
                  return sla ? (
                    <SlaChip state={sla} />
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  );
                })()}
              </td>
              <td className="max-w-44 px-3">
                {t.responsible ? (
                  <span className="flex items-center gap-1.5">
                    <Avatar className="size-5">
                      <AvatarFallback className="bg-primary/10 text-[9px] font-semibold text-primary">
                        {initials(t.responsible.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="truncate" title={t.responsible.name}>
                      {t.responsible.name}
                    </span>
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>
              {showClosedAt ? (
                <td className="whitespace-nowrap px-3 text-muted-foreground">
                  {formatDateTime(t.closedAt ?? t.canceledAt)}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Skeleton espelhando a estrutura carregada ───────────────────────────────

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-3" aria-busy="true">
      {[0, 1].map((g) => (
        <div key={g} className="flex flex-col gap-2">
          <div className="flex items-center gap-2 px-2">
            <Skeleton className="size-3.5 rounded-sm" />
            <Skeleton className="h-4 w-48" />
          </div>
          <div className="rounded-md border border-border/70">
            {[0, 1, 2, 3].map((r) => (
              <div
                key={r}
                className="flex items-center gap-4 border-b px-3 py-2.5 last:border-0"
              >
                <Skeleton className="h-4 w-14" />
                <Skeleton className="h-4 max-w-72 flex-1" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-[22px] w-16 rounded-[4px]" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
